import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Tag, Clock, ArrowRight } from 'lucide-react';
import { formatDate } from '../utils/helpers';
import { usePageTitle } from '../hooks/usePageTitle';

// --- IMPOR DATABASE ---
import facilitiesDataRaw from '../data/facilities.json';
import attractionsDataRaw from '../data/attractions.json';

const facilitiesList = Array.isArray(facilitiesDataRaw) ? facilitiesDataRaw : (facilitiesDataRaw?.default || []);
const attractionsList = Array.isArray(attractionsDataRaw) ? attractionsDataRaw : (attractionsDataRaw?.default || []);

// ─── PROMOTIONS PAGE ──────────────────────────────────────
const MOCK_PROMOS = [
  { id: 1, code: 'EARLY14', title_id: 'Early Booking Discount', title_en: 'Early Booking Discount', title_zh: '提前预订折扣', description_id: 'Pesan 14 hari sebelumnya dan hemat 10% untuk semua tipe kamar.', description_en: 'Book 14 days in advance and save 10% on all room types.', description_zh: '提前 14 天预订即可享受所有房型 10% 的折扣。', discount_percent: 10, valid_until: '2026-12-31', image: null },
  { id: 2, code: 'IJEN2026', title_id: 'Ijen Package', title_en: 'Ijen Package', title_zh: '宜珍火山套餐', description_id: '1 malam menginap + sarapan + tour guide Kawah Ijen. Mulai Rp 1.250.000/pax.', description_en: '1 night stay + breakfast + Kawah Ijen tour guide. From Rp 1,250,000/pax.', description_zh: '1 晚住宿 + 早餐 + 宜珍火山导游。起价 Rp 1,250,000/人。', discount_percent: 0, valid_until: '2026-12-31', image: null, is_package: true },
  { id: 3, code: 'LONGTAY3', title_id: 'Long Stay Deal', title_en: 'Long Stay Deal', title_zh: '长住优惠', description_id: 'Menginap minimal 3 malam dan nikmati diskon 15% otomatis.', description_en: 'Stay at least 3 nights and enjoy automatic 15% discount.', description_zh: '连住至少 3 晚即可自动享受 15% 的折扣。', discount_percent: 15, valid_until: '2026-12-31', image: null },
];

export function Promotions() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  usePageTitle(lang === 'id' ? 'Promosi & Diskon' : lang === 'zh' ? '促销与折扣' : 'Promotions');
  
  const [promos, setPromos] = useState(MOCK_PROMOS);

  useEffect(() => {
    try {
      if (typeof promotionsAPI !== 'undefined') {
        promotionsAPI.getAll().then((d) => { if (d?.length || d?.promotions?.length) setPromos(d.promotions || d); }).catch(() => {});
      }
    } catch (e) {}
  }, []);

  return (
    <div className="bg-cream min-h-screen">
      <div className="bg-primary-950 pt-32 pb-16 px-6 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23d4881c' fill-opacity='0.8'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4z'/%3E%3C/g%3E%3C/svg%3E")` }} />
        <div className="relative max-w-6xl mx-auto text-center z-10">
          <p className="text-accent-400 text-xs font-sans tracking-[0.3em] uppercase mb-3 font-bold">— {lang === 'id' ? 'Penawaran Eksklusif' : lang === 'zh' ? '独家优惠' : 'Exclusive Offers'} —</p>
          <h1 className="font-display text-5xl text-white font-bold mb-4">{t('promotions.title')}</h1>
          <p className="text-white/60 font-body text-lg max-w-xl mx-auto">{t('promotions.subtitle')}</p>
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {promos.map((promo) => {
            const title = lang === 'zh' ? (promo.title_zh || promo.title_en) : lang === 'en' ? promo.title_en : promo.title_id;
            const desc = lang === 'zh' ? (promo.description_zh || promo.description_en) : lang === 'en' ? promo.description_en : promo.description_id;
            
            return (
              <div key={promo.id} className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-xl transition-all hover:-translate-y-1 group">
                <div className="h-48 bg-gradient-to-br from-primary-800 to-earth-800 flex items-center justify-center relative">
                  {promo.image ? <img src={promo.image} alt="" className="w-full h-full object-cover" loading="lazy" /> : (
                    <div className="text-center text-white/50">
                      <Tag size={40} className="mx-auto mb-2" />
                      <span className="text-3xl font-display font-bold text-white">{promo.discount_percent > 0 ? `-${promo.discount_percent}%` : (lang === 'zh' ? '套餐' : 'PAKET')}</span>
                    </div>
                  )}
                  <div className="absolute top-4 right-4 bg-accent-500 text-white px-3.5 py-1.5 rounded-full text-xs font-sans font-bold shadow-md">{promo.code}</div>
                </div>
                <div className="p-6">
                  <h3 className="font-display text-xl text-dark font-bold mb-2">{title}</h3>
                  <p className="text-gray-500 text-sm font-sans mb-5 leading-relaxed">{desc}</p>
                  <div className="flex items-center gap-1.5 text-xs text-gray-400 font-sans mb-5 font-semibold">
                    <Clock size={14} /> {t('promotions.valid_until')}: {formatDate(promo.valid_until, lang)}
                  </div>
                  <Link to={`/booking?promo=${promo.code}`} className="block w-full text-center py-3 bg-primary-700 hover:bg-primary-600 text-white text-sm font-sans font-bold rounded-xl transition-colors shadow-sm">
                    {t('promotions.book_promo')}
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── RESTAURANT DATA ────────
const RESTAURANT_STORAGE_KEY = 'nm_restaurant_data';
const RESTAURANT_DEFAULTS = {
  gallery: [
    { id: 1, src: null, gradient: 'from-amber-900 via-yellow-800 to-amber-700', label_id: 'Suasana Pagi di Taman', label_en: 'Morning Garden Ambiance', label_zh: '早晨的花园氛围', emoji: '🌅' },
    { id: 2, src: null, gradient: 'from-primary-900 via-earth-800 to-primary-700', label_id: 'Meja Makan Outdoor', label_en: 'Outdoor Dining Table', label_zh: '户外餐桌', emoji: '🍽️' },
    { id: 3, src: null, gradient: 'from-earth-900 via-amber-800 to-earth-700', label_id: 'Bar & Coffee Corner', label_en: 'Bar & Coffee Corner', label_zh: '酒吧和咖啡角', emoji: '☕' },
    { id: 4, src: null, gradient: 'from-green-900 via-primary-800 to-green-700', label_id: 'Area Lounge & Kolam', label_en: 'Pool & Lounge Area', label_zh: '泳池和休息区', emoji: '🌿' },
  ],
  menu: {
    breakfast: {
      label_id: 'Sarapan', label_en: 'Breakfast', label_zh: '早餐', time_id: '07:00 – 10:00', time_en: '07:00 – 10:00', time_zh: '07:00 – 10:00', emoji: '🌅',
      items: [
        { id: 1, name_id: 'Nasi Goreng Spesial', name_en: 'Special Fried Rice', name_zh: '特色炒饭', desc_id: 'Bumbu Jawa dengan telur & kerupuk', desc_en: 'Javanese spiced, with egg & crackers', desc_zh: '爪哇香料，配鸡蛋和饼干', price: 25000, emoji: '🍳', gradient: 'from-orange-400 to-amber-500', badge_id: 'Favorit', badge_en: 'Favorite', badge_zh: '最爱' },
        { id: 2, name_id: 'Mie Goreng Jawa', name_en: 'Javanese Fried Noodles', name_zh: '爪哇炒面', desc_id: 'Bumbu khas Banyuwangi', desc_en: 'Banyuwangi style seasoning', desc_zh: '外南梦风味调料', price: 22000, emoji: '🍜', gradient: 'from-yellow-500 to-orange-400' },
        { id: 3, name_id: 'Bubur Ayam', name_en: 'Chicken Porridge', name_zh: '鸡肉粥', desc_id: 'Suwiran ayam & pelengkap', desc_en: 'Shredded chicken & condiments', desc_zh: '鸡肉丝及配料', price: 18000, emoji: '🥣', gradient: 'from-amber-300 to-yellow-400' },
        { id: 4, name_id: 'Roti Panggang & Selai', name_en: 'Toast & Homemade Jam', name_zh: '吐司和自制果酱', desc_id: 'Selai buah buatan sendiri', desc_en: 'Homemade tropical fruit jam', desc_zh: '自制热带水果果酱', price: 15000, emoji: '🍞', gradient: 'from-amber-600 to-yellow-500' },
      ],
    },
    main: {
      label_id: 'Menu Utama', label_en: 'Main Course', label_zh: '主菜', time_id: 'Sesuai permintaan', time_en: 'On request', time_zh: '按要求提供', emoji: '🍽️',
      items: [
        { id: 5, name_id: 'Ayam Bakar Bumbu Jawa', name_en: 'Javanese Grilled Chicken', name_zh: '爪哇烤鸡', desc_id: 'Bumbu kunci + lalapan segar', desc_en: 'Javanese marinade + fresh veggies', desc_zh: '爪哇腌料 + 新鲜蔬菜', price: 45000, emoji: '🍗', gradient: 'from-amber-700 to-orange-600', badge_id: 'Chef Pilihan', badge_en: "Chef's Pick", badge_zh: '厨师推荐' },
        { id: 6, name_id: 'Ikan Asam Manis', name_en: 'Sweet & Sour Fish', name_zh: '糖醋鱼', desc_id: 'Ikan laut segar, saus asam manis', desc_en: 'Fresh sea fish, sweet & sour sauce', desc_zh: '新鲜海鱼，糖醋汁', price: 50000, emoji: '🐟', gradient: 'from-blue-600 to-teal-500' },
        { id: 7, name_id: 'Tempe & Tahu Goreng', name_en: 'Crispy Tempe & Tofu', name_zh: '香脆丹贝和豆腐', desc_id: 'Crispy lokal dengan sambal', desc_en: 'Crispy local with chili sambal', desc_zh: '当地特色香脆配辣椒酱', price: 20000, emoji: '🫘', gradient: 'from-amber-800 to-yellow-700' },
        { id: 8, name_id: 'Sayur Lodeh', name_en: 'Javanese Vegetable Curry', name_zh: '爪哇蔬菜咖喱', desc_id: 'Kuah santan dengan sayuran segar', desc_en: 'Coconut milk with fresh vegetables', desc_zh: '椰奶配新鲜蔬菜', price: 18000, emoji: '🥬', gradient: 'from-green-600 to-emerald-500' },
      ],
    },
    beverages: {
      label_id: 'Minuman', label_en: 'Beverages', label_zh: '饮料', time_id: 'Sepanjang hari', time_en: 'All day', time_zh: '全天', emoji: '☕',
      items: [
        { id: 9, name_id: 'Kopi Robusta Banyuwangi', name_en: 'Banyuwangi Robusta', name_zh: '外南梦罗布斯塔咖啡', desc_id: 'Single origin lokal, seduhan manual', desc_en: 'Local single origin, hand-brewed', desc_zh: '当地单一产地，手工冲泡', price: 15000, emoji: '☕', gradient: 'from-amber-900 to-yellow-800', badge_id: 'Lokal', badge_en: 'Local', badge_zh: '当地的' },
        { id: 10, name_id: 'Teh Poci Jawa', name_en: 'Javanese Clay Pot Tea', name_zh: '爪哇茶壶', desc_id: 'Teh Jawa dalam teko tanah liat', desc_en: 'Javanese tea in traditional clay pot', desc_zh: '传统陶壶中的爪哇茶', price: 10000, emoji: '🫖', gradient: 'from-amber-600 to-orange-500' },
        { id: 11, name_id: 'Jus Buah Tropis', name_en: 'Tropical Fruit Juice', name_zh: '热带水果汁', desc_id: 'Alpukat, mangga, atau jambu segar', desc_en: 'Fresh avocado, mango, or guava', desc_zh: '新鲜鳄梨、芒果或番石榴', price: 18000, emoji: '🍹', gradient: 'from-green-500 to-teal-400' },
        { id: 12, name_id: 'Es Kelapa Muda', name_en: 'Young Coconut Ice', name_zh: '青椰子冰', desc_id: 'Kelapa muda segar dari kebun sekitar', desc_en: 'Fresh young coconut from nearby gardens', desc_zh: '附近花园的新鲜青椰子', price: 15000, emoji: '🥥', gradient: 'from-emerald-500 to-green-400' },
      ],
    },
  },
};

function useRestaurantPublicData() {
  const [data, setData] = React.useState(() => {
    try {
      const saved = localStorage.getItem(RESTAURANT_STORAGE_KEY);
      if (saved) return JSON.parse(saved);
    } catch {}
    return JSON.parse(JSON.stringify(RESTAURANT_DEFAULTS));
  });
  React.useEffect(() => {
    const api = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
    fetch(`${api}/content/cms/restaurant`)
      .then(r => r.json())
      .then(d => {
        if (d.main && Object.keys(d.main).length > 0) {
          setData(d.main);
          localStorage.setItem(RESTAURANT_STORAGE_KEY, JSON.stringify(d.main));
        }
      })
      .catch(() => {});
  }, []);
  return data;
}

export function Restaurant() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  usePageTitle(lang === 'id' ? 'Restoran & Kafe' : lang === 'zh' ? '餐厅与咖啡厅' : 'Restaurant & Café');
  const data = useRestaurantPublicData();
  const fmtPrice = (p) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(p);

  const menuSections = [
    { key: 'breakfast', ...data.menu?.breakfast },
    { key: 'main',      ...data.menu?.main },
    { key: 'beverages', ...data.menu?.beverages },
  ];

  return (
    <div className="bg-cream min-h-screen">
      {/* ── HERO ── */}
      <div className="bg-primary-950 pt-32 pb-20 px-6 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23d4881c' fill-opacity='0.8'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4z'/%3E%3C/g%3E%3C/svg%3E")` }} />
        <div className="relative max-w-6xl mx-auto text-center z-10">
          <p className="text-accent-400 text-xs font-sans tracking-[0.3em] uppercase mb-3 font-bold">— {lang === 'id' ? 'Cita Rasa Lokal' : lang === 'zh' ? '当地风味' : 'Local Flavors'} —</p>
          <h1 className="font-display text-5xl md:text-6xl text-white font-bold mb-4">{t('restaurant.title')}</h1>
          <p className="text-white/60 font-body text-lg max-w-xl mx-auto">{t('restaurant.subtitle')}</p>
        </div>
      </div>

      {/* ── INFO BAR ── */}
      <div className="max-w-4xl mx-auto px-6 pt-10 pb-2">
        <div className="bg-white shadow-sm border border-earth-100 rounded-3xl p-6 md:p-8 flex flex-col md:flex-row gap-6 items-center">
          <div className="w-16 h-16 bg-primary-50 rounded-2xl flex items-center justify-center text-3xl shrink-0 border border-primary-100">🍽️</div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-display text-2xl text-dark font-bold mb-2">
              {lang === 'id' ? 'Restoran & Coffee Corner' : lang === 'zh' ? '餐厅和咖啡角' : 'Restaurant & Coffee Corner'}
            </h3>
            <p className="text-gray-600 font-sans text-sm">
              {lang === 'id'
                ? 'Buka setiap hari · Sarapan 07:00–10:00 · Makan siang & malam tersedia sesuai permintaan'
                : lang === 'zh' ? '每天营业 · 早餐 07:00–10:00 · 午餐和晚餐按要求提供' : 'Open daily · Breakfast 07:00–10:00 · Lunch & dinner available on request'}
            </p>
          </div>
          <a href="https://wa.me/6281378575758"
            target="_blank" rel="noopener noreferrer"
            className="shrink-0 inline-flex items-center gap-2 px-6 py-3 bg-[#25D366] hover:bg-[#128C7E] text-white rounded-xl font-sans text-sm font-bold transition-colors shadow-md">
            💬 {lang === 'id' ? 'Pesan via WA' : lang === 'zh' ? '通过 WA 预订' : 'Order via WA'}
          </a>
        </div>
      </div>

      {/* ── MENU SECTIONS ── */}
      <div className="max-w-4xl mx-auto px-6 py-10 space-y-16">
        {menuSections.map((section) => (
          <div key={section.key}>
            <div className="flex items-center gap-4 mb-8 border-b border-earth-100 pb-4">
              <span className="text-4xl">{section.emoji}</span>
              <div>
                <h2 className="font-display text-3xl text-dark font-bold">
                  {lang === 'zh' ? (section.label_zh || section.label_en) : lang === 'en' ? section.label_en : section.label_id}
                </h2>
                <p className="text-sm text-primary-600 font-sans font-bold mt-1">
                  🕐 {lang === 'zh' ? (section.time_zh || section.time_en) : lang === 'en' ? section.time_en : section.time_id}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {(section.items || []).map((item) => {
                const name = lang === 'zh' ? (item.name_zh || item.name_en) : lang === 'en' ? item.name_en : item.name_id;
                const desc = lang === 'zh' ? (item.desc_zh || item.desc_en) : lang === 'en' ? item.desc_en : item.desc_id;
                const badge = lang === 'zh' ? (item.badge_zh || item.badge_en) : lang === 'en' ? item.badge_en : item.badge_id;

                return (
                  <div key={item.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm hover:shadow-md transition-all flex">
                    <div className={`w-24 shrink-0 bg-gradient-to-br ${item.gradient} flex items-center justify-center`}>
                      <span className="text-4xl">{item.emoji}</span>
                    </div>
                    <div className="p-5 flex-1 min-w-0 flex flex-col justify-center">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <h4 className="font-display text-lg font-bold text-dark leading-snug">{name}</h4>
                        {badge && (
                          <span className="shrink-0 text-[10px] px-2.5 py-1 bg-accent-100 text-accent-800 rounded-lg font-sans font-bold uppercase tracking-wider">
                            {badge}
                          </span>
                        )}
                      </div>
                      <p className="text-gray-500 text-xs font-sans leading-relaxed mb-3 flex-1">{desc}</p>
                      <p className="text-primary-800 font-display text-xl font-bold">{fmtPrice(item.price)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── MEETING & EVENTS PAGE ─────────────────────────────────
const MEETING_STORAGE_KEY = 'nm_meeting_data';
const MEETING_DEFAULTS = {
  hero: {
    subtitle_id: 'Ruang gathering intim di tengah taman asri Banyuwangi. Sempurna untuk arisan, rapat kecil, reuni, hingga perayaan spesial.',
    subtitle_en: 'An intimate gathering space nestled in a lush garden. Perfect for family gatherings, small meetings, and special celebrations.',
    subtitle_zh: '坐落在郁郁葱葱的花园中的私密聚会空间。非常适合家庭聚会、小型会议和特别庆祝活动。'
  },
  intro: {
    title_id: 'Garden Intimate Event Space',
    title_en: 'Garden Intimate Event Space',
    title_zh: '花园私密活动空间',
    desc_id: 'Kami menyediakan ruang acara yang hangat dan personal, dikelilingi taman hijau yang asri. Setiap acara ditangani dengan sepenuh hati.',
    desc_en: 'We provide a warm and personal event space, surrounded by lush green gardens. Every event is handled with care.',
    desc_zh: '我们提供温馨私人的活动空间，周围环绕着郁郁葱葱的绿色花园。每场活动我们都用心处理。',
    image: null, capacity: '20–150',
    location_id: 'Taman & Area Outdoor', location_en: 'Garden & Outdoor Area', location_zh: '花园及户外区'
  },
  features: [
    { id: 1, icon: '🪑', label_id: 'Kursi & Meja', label_en: 'Chairs & Tables', label_zh: '桌椅' },
    { id: 2, icon: '🔊', label_id: 'Sound System', label_en: 'Sound System', label_zh: '音响系统' },
    { id: 3, icon: '☕', label_id: 'Coffee Break', label_en: 'Coffee Break', label_zh: '茶歇' },
    { id: 4, icon: '🌿', label_id: 'Dekorasi Taman', label_en: 'Garden Decor', label_zh: '花园装饰' },
    { id: 5, icon: '📶', label_id: 'Akses WiFi', label_en: 'WiFi Access', label_zh: '无线网络' },
    { id: 6, icon: '📸', label_id: 'Dokumentasi', label_en: 'Documentation', label_zh: '活动记录' },
  ],
  packages: [
    { id: 1, icon: '💼', name_id: 'Paket Bisnis Harian', name_en: 'Daily Business Package', name_zh: '每日商务套餐', desc_id: 'Ruang meeting 20 orang, proyektor, sound system, snack', desc_en: 'Meeting room for 20 pax, projector, sound system, snacks', desc_zh: '20人会议室，投影仪，音响系统，零食', price: 1500000, badge_id: '', badge_en: '', badge_zh: '', gradient: 'from-amber-800 to-amber-600' },
    { id: 2, icon: '🌿', name_id: 'Paket Gathering', name_en: 'Gathering Package', name_zh: '聚会套餐', desc_id: 'Area outdoor 50–100 orang, dekorasi, katering', desc_en: 'Outdoor area for 50–100 pax, decoration, catering', desc_zh: '50-100 人户外区域，装饰，餐饮', price: 5000000, badge_id: 'Populer', badge_en: 'Popular', badge_zh: '热门', gradient: 'from-primary-900 to-primary-700' },
    { id: 3, icon: '💐', name_id: 'Paket Pernikahan', name_en: 'Wedding Package', name_zh: '婚礼套餐', desc_id: 'Intimate wedding di taman hijau, kapasitas 150 tamu', desc_en: 'Intimate garden wedding for up to 150 guests', desc_zh: '绿色花园的私密婚礼，最多容纳150名宾客', price: 25000000, badge_id: 'Premium', badge_en: 'Premium', badge_zh: '优质的', gradient: 'from-earth-900 to-earth-700' },
  ],
  whatsapp: '6281378575758',
};

function useMeetingData() {
  const [data, setData] = React.useState(() => {
    try {
      const saved = localStorage.getItem(MEETING_STORAGE_KEY);
      if (saved) return JSON.parse(saved);
    } catch {}
    return JSON.parse(JSON.stringify(MEETING_DEFAULTS));
  });
  React.useEffect(() => {
    const api = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
    fetch(`${api}/content/cms/meeting`)
      .then(r => r.json())
      .then(d => { if (d.main && Object.keys(d.main).length > 0) { setData(d.main); localStorage.setItem(MEETING_STORAGE_KEY, JSON.stringify(d.main)); } })
      .catch(() => {});
  }, []);
  return data;
}

export function MeetingEvents() {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  usePageTitle(lang === 'id' ? 'Meeting & Acara' : lang === 'zh' ? '会议与活动' : 'Meeting & Events');
  const data = useMeetingData();

  const fmtPrice = (p) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(p);
  const waBase = `https://wa.me/${data.whatsapp || '6281378575758'}`;

  return (
    <div className="bg-cream min-h-screen">
      <div className="bg-primary-950 pt-32 pb-20 px-6 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23d4881c' fill-opacity='0.8'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4z'/%3E%3C/g%3E%3C/svg%3E")` }} />
        <div className="relative max-w-4xl mx-auto text-center z-10">
          <p className="text-accent-400 text-xs font-sans tracking-[0.3em] uppercase mb-4 font-bold">— {lang === 'id' ? 'Acara & Pertemuan' : lang === 'zh' ? '活动与聚会' : 'Events & Gatherings'} —</p>
          <h1 className="font-display text-5xl md:text-6xl text-white font-bold mb-5">Meeting & Events</h1>
          <p className="text-white/60 font-sans text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            {lang === 'zh' ? data.hero?.subtitle_zh : lang === 'en' ? data.hero?.subtitle_en : data.hero?.subtitle_id}
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div className="relative">
            {data.intro?.image ? (
              <img src={data.intro.image} alt="Event Space" className="w-full h-96 object-cover rounded-3xl shadow-xl" />
            ) : (
              <div className="w-full h-[400px] bg-gradient-to-br from-primary-900 via-earth-800 to-primary-800 rounded-3xl shadow-xl flex flex-col items-center justify-center gap-4">
                <span className="text-6xl">🌿</span>
                <span className="text-white/40 font-display text-2xl font-bold">Garden Event Space</span>
              </div>
            )}
            <div className="absolute -bottom-6 -right-6 bg-white rounded-3xl shadow-xl p-6 text-center hidden md:block border border-gray-100">
              <div className="font-display text-3xl font-bold text-primary-800 mb-1">{data.intro?.capacity || '20–150'}</div>
              <div className="text-xs font-sans text-gray-500 font-bold uppercase tracking-wider">{lang === 'id' ? 'Kapasitas Tamu' : lang === 'zh' ? '宾客容量' : 'Guest Capacity'}</div>
            </div>
          </div>

          <div>
            <p className="text-accent-500 text-xs font-sans tracking-widest uppercase mb-3 font-bold">
              {lang === 'id' ? 'Tentang Ruang Kami' : lang === 'zh' ? '关于我们的空间' : 'About Our Space'}
            </p>
            <h2 className="font-display text-3xl md:text-4xl text-dark font-bold mb-5 leading-snug">
              {lang === 'zh' ? data.intro?.title_zh : lang === 'en' ? data.intro?.title_en : data.intro?.title_id}
            </h2>
            <p className="text-gray-500 font-sans text-base leading-relaxed mb-8">
              {lang === 'zh' ? data.intro?.desc_zh : lang === 'en' ? data.intro?.desc_en : data.intro?.desc_id}
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {(data.features || []).map(f => (
                <div key={f.id} className="flex flex-col items-center text-center gap-2 bg-white rounded-2xl p-4 border border-gray-100 shadow-sm hover:border-primary-200 transition-colors">
                  <span className="text-2xl">{f.icon}</span>
                  <span className="text-xs font-sans text-gray-600 font-bold">{lang === 'zh' ? f.label_zh : lang === 'en' ? f.label_en : f.label_id}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 pb-20">
        <div className="text-center mb-12">
          <p className="text-accent-500 text-xs font-sans tracking-widest uppercase mb-2 font-bold">
            {lang === 'id' ? 'Paket Tersedia' : lang === 'zh' ? '我们的套餐' : 'Our Packages'}
          </p>
          <h2 className="font-display text-4xl text-dark font-bold">
            {lang === 'id' ? 'Pilih Paket yang Sesuai' : lang === 'zh' ? '选择您的套餐' : 'Choose Your Package'}
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {(data.packages || []).map(pkg => {
            const name = lang === 'zh' ? pkg.name_zh : lang === 'en' ? pkg.name_en : pkg.name_id;
            const desc = lang === 'zh' ? pkg.desc_zh : lang === 'en' ? pkg.desc_en : pkg.desc_id;
            const badge = lang === 'zh' ? pkg.badge_zh : lang === 'en' ? pkg.badge_en : pkg.badge_id;
            return (
              <div key={pkg.id} className="bg-white rounded-3xl border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-2 transition-all overflow-hidden flex flex-col">
                <div className={`bg-gradient-to-br ${pkg.gradient} px-8 py-10 relative text-center`}>
                  {badge && (
                    <span className="absolute top-4 right-4 bg-accent-500 text-white text-[10px] font-sans font-bold px-3 py-1.5 rounded-full uppercase tracking-widest shadow-md">
                      {badge}
                    </span>
                  )}
                  <span className="text-5xl mb-4 block">{pkg.icon}</span>
                  <h3 className="font-display text-2xl text-white font-bold">{name}</h3>
                </div>
                <div className="p-8 flex-1 flex flex-col">
                  <p className="text-gray-500 font-sans text-sm leading-relaxed flex-1 text-center mb-6">
                    {desc}
                  </p>
                  <div className="text-center mb-6">
                    <p className="text-xs font-sans text-gray-400 mb-1 font-semibold uppercase tracking-wider">{lang === 'id' ? 'Mulai dari' : lang === 'zh' ? '起价' : 'Starting from'}</p>
                    <p className="font-display text-3xl text-primary-800 font-bold">{fmtPrice(pkg.price)}</p>
                  </div>
                  <a href={`${waBase}?text=${encodeURIComponent(lang === 'id' ? `Halo, saya ingin info Paket ${name}` : `Hello, I want info about ${name}`)}`} target="_blank" rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full py-3.5 bg-[#25D366] hover:bg-[#128C7E] text-white font-sans font-bold text-sm rounded-xl transition-colors shadow-md">
                    💬 {lang === 'id' ? 'Tanya via WhatsApp' : lang === 'zh' ? '通过 WA 咨询' : 'Ask via WhatsApp'}
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── ATTRACTIONS PAGE ──────────────────────────────────────

const AttractionCard = ({ place, lang }) => {
  const [isHovered, setIsHovered] = useState(false);
  const videoRef = useRef(null);

  const handleMouseEnter = () => {
    setIsHovered(true);
    if (videoRef.current) {
      videoRef.current.play().catch(e => console.log("Autoplay dicegah oleh browser:", e));
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    if (videoRef.current) {
      videoRef.current.pause();
      // Mengembalikan durasi video ke detik 0 jika kursor dilepas
      videoRef.current.currentTime = 0; 
    }
  };

  const name = lang === 'zh' ? place.name_zh : (lang === 'en' ? (place.name_en || place.name_id) : place.name_id);
  const tagline = lang === 'zh' ? place.tagline_zh : (lang === 'en' ? place.tagline_en : place.tagline_id);
  
  // PERBAIKAN: Cari item pertama yang BUKAN video untuk dijadikan gambar sampul (cover)
  const bgImage = place.photos?.find(p => p.type !== 'video')?.src || '/placeholder-wisata.jpg';

  return (
    <Link 
      to={`/attractions/${place.slug}`} 
      className="group relative h-[400px] rounded-3xl overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-500 block bg-gray-900"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* 1. Gambar Background Statis (Sekarang 100% memuat gambar, bukan video) */}
      <img 
        src={bgImage} 
        alt={name} 
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-in-out z-0 
          ${isHovered && place.video_hover ? 'opacity-0' : 'opacity-100'}`} 
      />
      
      {/* 2. Video Hover (Hanya muncul dan play saat kursor masuk) */}
      {place.video_hover && (
        <video
          ref={videoRef}
          src={place.video_hover}
          muted
          loop
          playsInline
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-in-out z-0 
            ${isHovered ? 'opacity-100' : 'opacity-0'}`}
        />
      )}

      {/* Dark Gradient Overlay (Tetap terlihat agar tulisan terbaca) */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/30 to-transparent z-10"></div>

      {/* Konten Text */}
      <div className="absolute inset-0 flex flex-col justify-end p-7 z-20">
        <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/10 backdrop-blur-md rounded-lg w-fit mb-3 border border-white/20">
          <span className="text-[10px] font-sans font-bold text-white tracking-widest uppercase">
            ± {place.distance} KM {lang === 'id' ? 'DARI SINI' : lang === 'zh' ? '距此' : 'FROM HERE'}
          </span>
        </div>

        <h3 className="font-display text-2xl text-white font-bold mb-2 group-hover:text-accent-400 transition-colors">
          {name}
        </h3>
        
        <p className="text-white/70 font-sans text-sm line-clamp-2 mb-5 leading-relaxed">
          {tagline}
        </p>
        
        <div className="inline-flex items-center gap-1.5 text-xs font-sans font-bold text-accent-400 uppercase tracking-widest group/link">
          {lang === 'id' ? 'Lihat Detail' : lang === 'zh' ? '查看详情' : 'View Details'} 
          <ArrowRight size={14} className="group-hover/link:translate-x-1 transition-transform" />
        </div>
      </div>
    </Link>
  );
};

// ─── HALAMAN UTAMA ATTRACTIONS ───
export function Attractions() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'id';
  usePageTitle(lang === 'id' ? 'Wisata Terdekat' : lang === 'zh' ? '附近景点' : 'Nearby Attractions');

  return (
    <div className="bg-cream min-h-screen pb-24 overflow-x-hidden">
      {/* ── HERO ── */}
      <div className="bg-primary-950 pt-36 pb-20 px-6 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4z' fill='%23ffffff'/%3E%3C/svg%3E")` }} />
        <div className="relative max-w-6xl mx-auto text-center z-10">
          <p className="text-accent-400 text-xs font-sans tracking-[0.3em] uppercase mb-3 font-bold">— {lang === 'id' ? 'Jelajahi Banyuwangi' : lang === 'zh' ? '探索外南梦' : 'Explore Banyuwangi'} —</p>
          <h1 className="font-display text-5xl md:text-6xl text-white font-bold mb-4">{t('attractions.title', { defaultValue: 'Wisata Terdekat' })}</h1>
          <p className="text-white/60 font-body text-lg max-w-xl mx-auto font-light leading-relaxed">{t('attractions.subtitle', { defaultValue: 'Banyuwangi menyimpan keajaiban alam dan budaya.' })}</p>
        </div>
      </div>

      {/* ── GRID WISATA DENGAN VIDEO HOVER ── */}
      <div className="max-w-6xl mx-auto px-6 py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {attractionsList.map((place) => (
          <AttractionCard key={place.slug} place={place} lang={lang} />
        ))}
      </div>
    </div>
  );
}

// ─── FACILITIES PAGE ───────────────────────────────────────
export function FacilitiesPage() {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  usePageTitle(lang === 'id' ? 'Fasilitas Hotel – Ndalem Mataram' : lang === 'zh' ? '酒店设施 – Ndalem Mataram' : 'Hotel Facilities – Ndalem Mataram');

  return (
    <div className="bg-cream min-h-screen pb-24 overflow-x-hidden">
      <section className="bg-primary-950 pt-36 pb-20 px-6 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23d4881c' fill-opacity='0.8'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4z'/%3E%3C/g%3E%3C/svg%3E")` }} />
        <div className="relative max-w-4xl mx-auto text-center space-y-3 z-10">
          <p className="text-accent-400 text-xs font-sans tracking-[0.3em] uppercase font-bold">
            — {lang === 'id' ? 'Kenyamanan Lengkap' : lang === 'zh' ? '全面舒适' : 'Complete Comfort'} —
          </p>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-white font-bold tracking-tight">
            {lang === 'id' ? 'Fasilitas Hotel' : lang === 'zh' ? '酒店设施' : 'Hotel Facilities'}
          </h1>
          <p className="text-white/60 font-body text-base md:text-lg max-w-xl mx-auto font-light leading-relaxed">
            {lang === 'id' 
              ? 'Semua yang Anda butuhkan tersedia di Ndalem Mataram untuk pengalaman menginap yang sempurna dan tak terlupakan.' 
              : lang === 'zh' 
                ? 'Ndalem Mataram 提供您所需的一切，确保您拥有完美而难忘的住宿体验。' 
                : 'Everything you need is available at Ndalem Mataram for a perfect and unforgettable stay.'}
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 mt-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {facilitiesList.map((facility) => {
            const name = lang === 'zh' ? (facility.name_zh || facility.name_en || facility.name_id) : lang === 'en' ? (facility.name_en || facility.name_id) : facility.name_id;
            const tagline = lang === 'zh' ? (facility.tagline_zh || facility.tagline_en || facility.tagline_id) : lang === 'en' ? (facility.tagline_en || facility.tagline_id) : facility.tagline_id;
            const hours = lang === 'zh' ? (facility.hours_zh || facility.hours_en || facility.hours_id) : lang === 'en' ? (facility.hours_en || facility.hours_id) : facility.hours_id;
            const bgImage = facility.photos && facility.photos.length > 0 ? facility.photos[0].src : '/placeholder-image.jpg';

            return (
              <Link to={`/facilities/${facility.slug}`} key={facility.slug} className="group relative h-[380px] rounded-3xl overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-500 block">
                <img src={bgImage} alt={name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-in-out bg-gray-200" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent transition-opacity duration-300 group-hover:opacity-90"></div>
                <div className="absolute inset-0 flex flex-col justify-end p-6">
                  <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/20 backdrop-blur-md rounded-lg w-fit mb-3 border border-white/30">
                    <Clock size={12} className="text-white" />
                    <span className="text-[10px] font-sans font-bold text-white tracking-wide uppercase">{hours}</span>
                  </div>
                  <h3 className="font-display text-2xl text-white font-bold mb-2 group-hover:text-accent-400 transition-colors">{name}</h3>
                  <p className="text-white/80 font-sans text-sm line-clamp-2 mb-5 leading-relaxed">{tagline}</p>
                  <div className="inline-flex items-center gap-2 text-xs font-sans font-bold text-accent-400 uppercase tracking-widest group/link">
                    {lang === 'id' ? 'Lihat Detail' : lang === 'zh' ? '查看详情' : 'View Details'}
                    <ArrowRight size={14} className="group-hover/link:translate-x-1.5 transition-transform" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>
    </div>
  );
}